import React, { useState, useMemo, useEffect } from 'react';
// Import Link from react-router-dom
import { Link, useLocation } from 'react-router-dom';
import { meditationData } from '../../data/meditationData'; // Asegúrate que la ruta sea correcta
import { useAuth } from '../../context/AuthContext';
import UrlConfig from '../../data/UrlConfig';
import { postApi } from '../../services/apiService';
import {
    MagnifyingGlassIcon,
    ChevronDownIcon,
    LockClosedIcon,
    CheckCircleIcon,
    UserPlusIcon
} from '@heroicons/react/24/solid';
import CommentSection from '../../components/comments/CommentSection';

// --- Helper: Genera un ID amigable para el scroll ---
const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

// --- Helper Component: Video Player (MODIFIED) ---
const VideoPlayer = ({ meditation, user }) => {
    const isLocked = meditation.requiresAuth && !user;

    return (
        <div className="relative aspect-video w-full rounded-lg overflow-hidden border border-gray-700 mt-4">
            {isLocked && (
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center z-20 p-4">
                    <LockClosedIcon className="w-12 h-12 text-indigo-400 mb-3" />
                    <h4 className="text-xl font-bold text-white text-center">Exclusive Access</h4>
                    <p className="text-gray-300 text-center mt-1 mb-4">Become a member to unlock this meditation.</p>
                    {/* --- ADDED BUTTON --- */}
                    <Link
                        to="/signup?fr=med_exc"
                        className="inline-flex items-center justify-center gap-2 py-2 px-5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-500 transition-all shadow-lg transform hover:scale-105"
                    >
                        Sign Up to Unlock
                    </Link>
                </div>
            )}
            {meditation.type === 'youtube' ? (
                <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${meditation.youtubeId}`}
                    title={meditation.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                ></iframe>
            ) : (
                <video
                    key={meditation.videoUrl}
                    className="w-full h-full"
                    controls={!isLocked}
                    playsInline
                    preload="metadata"
                >
                    <source src={meditation.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            )}
        </div>
    );
};


// --- Helper Component: Card for Meditations ---
// Sin cambios aquí
const MeditationCard = ({ meditation, user }) => {
    const isLocked = meditation.requiresAuth && !user;

    return (
        <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 shadow-lg flex flex-col">
            <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                    <meditation.Icon className={`w-10 h-10 ${meditation.iconColor}`} />
                    {isLocked && (
                        <div className="absolute -bottom-1 -right-1 flex items-center justify-center w-5 h-5 bg-black/80 backdrop-blur-sm rounded-full border border-amber-500/50">
                            <LockClosedIcon className="w-3 h-3 text-amber-400" />
                        </div>
                    )}
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">{meditation.title}</h3>
                    <p className="text-gray-400 mt-1">{meditation.description}</p>
                </div>
            </div>
            <VideoPlayer meditation={meditation} user={user} />
        </div>
    );
};


// --- Main Component ---
const HealingMeditations = () => {
    const { user } = useAuth();
    const location = useLocation();

    const [searchTerm, setSearchTerm] = useState('');
    const [openCategory, setOpenCategory] = useState(null);
    const [titleGlowIntensity, setTitleGlowIntensity] = useState(0.2);

    // Form states
    const [name, setName] = useState(localStorage.getItem('track_user_name') || '');
    const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            document.documentElement.style.scrollBehavior = 'auto';
        };
    }, []);
    
    useEffect(() => {
        const animateGlow = () => {
          setTitleGlowIntensity(prev => (prev === 0.2 ? 0.4 : 0.2));
        };
        const glowInterval = setInterval(animateGlow, 3000);
        return () => clearInterval(glowInterval);
    }, []);

    const availableMeditations = useMemo(() => {
        return meditationData;
    }, []);

    const filteredMeditations = useMemo(() => {
        if (!searchTerm.trim()) return availableMeditations;
        return availableMeditations.filter(meditation =>
            meditation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            meditation.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            meditation.category.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, availableMeditations]);

    const groupedMeditations = useMemo(() => {
        const groups = filteredMeditations.reduce((acc, meditation) => {
            const category = meditation.category;
            if (!acc[category.id]) {
                acc[category.id] = {
                    ...category,
                    meditations: []
                };
            }
            acc[category.id].meditations.push(meditation);
            return acc;
        }, {});
        if (openCategory === null && Object.keys(groups).length > 0) {
            setOpenCategory(Object.keys(groups)[0]);
        }
        return groups;
    }, [filteredMeditations, openCategory]); 

    const handleToggleCategory = (categoryId) => {
        setOpenCategory(prev => (prev === categoryId ? null : categoryId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');

        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address.');
            setIsLoading(false);
            return;
        }

        name && localStorage.setItem('track_user_name', name.trim());
        email && localStorage.setItem('track_user_email', email.trim());

        const requestBody = { 
          email, 
          nick: name || 'Friend', 
          folder: 'healing-meditations', 
          page: location.pathname, 
          lang: 'en', 
          newsletter: ['healing-meditations-lead'] 
        };

        try {
            await postApi(UrlConfig.leads_add, requestBody);
            setMessage('Thank you! Your gifts and the full transmissions are on their way to your inbox.');
            setSubmitted(true);
        } catch (err) {
            setError(err.message || 'An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="relative bg-black text-white min-h-screen font-sans">
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>

            <main className="relative z-10 container mx-auto px-4 py-16">
                <h1
                     style={{
                        fontFamily: '"Cinzel Decorative", serif',
                        backgroundImage: 'linear-gradient(to bottom, #BEB6F5, #A79AF0, #8C7AEB, #7F6AE6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: `0 0 8px rgba(190, 182, 245, ${titleGlowIntensity}), 0 0 12px rgba(190, 182, 245, ${titleGlowIntensity / 2})`,
                        transition: 'text-shadow 1.5s ease-in-out'
                    }}
                    className="text-4xl sm:text-5xl font-bold text-center mb-4 leading-tight"
                >
                    A Collection of Healing Meditations
                </h1>
                <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mx-auto mb-12">
                    Find a quiet space, use headphones, and allow these frequencies to guide you toward healing and transformation.
                </p>

                {/* --- Search Bar --- */}
                <div className="max-w-xl mx-auto mb-8">
                     <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Search all meditations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-gray-900/70 border border-gray-700 text-white rounded-lg pl-11 pr-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
                        />
                    </div>
                </div>

                {/* --- Collapsible Index (MODIFICADO) --- */}
                <div className="max-w-xl mx-auto w-full space-y-2 mb-20">
                    {Object.entries(groupedMeditations).length > 0 ? (
                        Object.entries(groupedMeditations).map(([categoryId, categoryData]) => (
                            <div key={categoryId} className="bg-gray-900/50 border border-gray-800 rounded-lg">
                                <button
                                    onClick={() => handleToggleCategory(categoryId)}
                                    className="w-full flex justify-between items-center p-3 text-left hover:bg-gray-800/60"
                                >
                                    <div className="flex items-center gap-3">
                                        <categoryData.Icon className={`w-5 h-5 ${categoryData.iconColor} flex-shrink-0`} />
                                        <h2 className="text-lg font-semibold text-gray-200">{categoryData.title}</h2>
                                    </div>
                                    <ChevronDownIcon
                                        className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${openCategory === categoryId ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {openCategory === categoryId && (
                                    <div className="border-t border-gray-800">
                                        <ul className="py-2">
                                            {categoryData.meditations.map(meditation => {
                                                const isLocked = meditation.requiresAuth && !user;
                                                return (
                                                    <li key={meditation.title}>
                                                        <a
                                                            href={`#${slugify(meditation.title)}`}
                                                            className="flex items-center gap-3 text-gray-300 hover:bg-indigo-900/50 hover:text-white transition-colors duration-200 text-sm px-4 py-2"
                                                        >
                                                            <div className="relative flex-shrink-0">
                                                                <meditation.Icon className="w-4 h-4 text-white/70" />
                                                                {isLocked && (
                                                                    <div className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center w-3.5 h-3.5 bg-gray-800/75 rounded-full border border-amber-600/70">
                                                                        <LockClosedIcon className="w-2 h-2 text-amber-400" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            <span className="flex-grow">{meditation.title}</span>

                                                            {isLocked && (
                                                                <span className="ml-auto text-xs font-semibold text-amber-500/90 bg-amber-900/60 px-2 py-0.5 rounded-full">
                                                                    Members Only
                                                                </span>
                                                            )}
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <p className="text-center text-gray-400 py-4">No meditations found for your search.</p>
                    )}
                </div>

                {/* --- Main Content: Meditation Cards --- */}
                <div className="w-full max-w-4xl mx-auto space-y-12 mb-20">
                    {filteredMeditations.length > 0 ? (
                        filteredMeditations.map(meditation => (
                            <div key={slugify(meditation.title)} id={slugify(meditation.title)} className="scroll-mt-20">
                                <MeditationCard meditation={meditation} user={user} />
                            </div>
                        ))
                    ) : (
                        !searchTerm && (
                            <div className="text-center py-16 px-6 bg-gray-900/50 rounded-lg border border-gray-800">
                                 <p className="text-2xl font-semibold text-gray-400">No meditations available.</p>
                            </div>
                        )
                    )}
                </div>

                {/* --- Lead Form & Member Message --- */}
                <div className="w-full max-w-4xl mx-auto text-center">
                    {!user && (
                        <>
                            {submitted ? (
                                <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
                                    <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
                                    <p className="text-xl font-semibold">{message}</p>
                                    <p className="text-lg mt-2">Check your email for access details.</p>
                                </div>
                            ) : (
                                <div className="p-8 bg-black/40 rounded-xl border-2 border-indigo-700/60 shadow-lg backdrop-blur-sm">
                                    <UserPlusIcon className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                                    <h3 className="text-3xl font-semibold mb-3">Unlock the Full Healing Meditations Transmissions</h3>
                                    <p className="text-gray-400 max-w-2xl mx-auto mb-6">
                                        Enter your email to receive the complete, full-length versions of these meditations, plus many more guided sessions.
                                    </p>
                                    <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-lg mx-auto">
                                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your Name (optional)" disabled={isLoading} className="bg-gray-900/50 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition disabled:opacity-50" />
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your Email Address" required disabled={isLoading} className="bg-gray-900/50 border border-gray-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none transition disabled:opacity-50" />
                                        {error && <p className="text-sm text-center text-red-400">{error}</p>}
                                        <button type="submit" disabled={isLoading} className="w-full inline-flex items-center justify-center gap-2 py-3 px-8 rounded-lg bg-indigo-700/70 border border-indigo-600 text-white font-bold text-lg hover:bg-indigo-600 transition-all shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isLoading ? 'Processing...' : 'Get Full Access'}
                                        </button>
                                    </form>
                                    <p className="text-xs text-gray-600 mt-4">We respect your privacy. No spam, ever.</p>
                                </div>
                            )}
                        </>
                    )}
                    {user && (
                        <div className="p-6 bg-gray-800/50 rounded-lg border border-green-500/30 shadow-md">
                            <p className="text-green-300 text-xl font-semibold">
                                As a member, you have full access to our entire library of meditations. Enjoy!
                            </p>
                        </div>
                    )}
                </div>
                <CommentSection
                    threadId={'meditation-healing-111'}
                />
            </main>

            <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
                © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
            </footer>
        </div>
    );
};

export default HealingMeditations;